import React, { useState, useCallback } from 'react';
import { Card, Descriptions, Tag, Button, Typography, Divider, Timeline, Space, message, Modal, Result, Empty, Input, Select, Form } from 'antd';
import { ArrowLeftOutlined, ClockCircleOutlined, SyncOutlined, CheckCircleOutlined, MinusCircleOutlined, UndoOutlined, EditOutlined, DeleteOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending:    { label: '待处理', color: 'blue', icon: <ClockCircleOutlined /> },
  processing: { label: '处理中', color: 'orange', icon: <SyncOutlined spin /> },
  resolved:   { label: '已解决', color: 'green', icon: <CheckCircleOutlined /> },
  closed:     { label: '已关闭', color: 'default', icon: <MinusCircleOutlined /> },
};

const CATEGORY_LABEL = {
  techSupport: '技术对接',
  bug: 'BUG',
  requirement: '需求',
  other: '其他',
};

const PRIORITY_MAP = {
  low:    { label: '低',   color: 'default' },
  normal: { label: '普通', color: 'blue' },
  high:   { label: '高',   color: 'orange' },
  urgent: { label: '紧急', color: 'red' },
};

// 所属App展示格式：App应用名称-系统-区域
// TODO: 联调时替换为接口数据
const APP_DISPLAY_MAP = {
  'app_qx-iOS-GL': '牵心Pro-iOS-全球',
  'app_qx-Android-GL': '牵心Pro-Android-全球',
  'app_hs-iOS-CN': 'HomeStar-iOS-国内',
  'app_hs-Android-CN': 'HomeStar-Android-国内',
  'app_hs-iOS-OV': 'HomeStar-iOS-海外',
};

const REMARK_TYPE_MAP = {
  enterprise: { label: '企业', color: 'blue' },
  staff:      { label: '客服', color: 'green' },
  system:     { label: '系统', color: 'default' },
};

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const MOCK_TICKETS = {
  'TK202607270001': { ticketNo: 'TK202607270001', title: 'test-SDK初始化接口返回错误码1001', category: 'techSupport', priority: 'urgent', status: 'pending', appValue: 'app_qx-iOS-GL', projectName: 'SDK联调项目', description: '调用SDK初始化接口时返回错误码1001...', attachments: [{ name: '错误日志.txt' }], createdAt: '2026-07-27 09:15', updatedAt: '2026-07-27 09:15' },
  'TK202607260002': { ticketNo: 'TK202607260002', title: 'test-设备固件OTA升级失败', category: 'bug', priority: 'high', status: 'processing', appValue: 'app_hs-Android-CN', projectName: '私有化部署', description: '设备型号X200，固件版本v2.3→v2.5升级过程中变砖。', attachments: [], createdAt: '2026-07-26 14:30', updatedAt: '2026-07-27 10:00' },
  'TK202607250003': { ticketNo: 'TK202607250003', title: 'test-App品牌配置页需求', category: 'requirement', priority: 'normal', status: 'resolved', appValue: 'app_qx-Android-GL', projectName: '', description: '希望在App OEM定制中增加品牌配置页面。', attachments: [], createdAt: '2026-07-25 11:00', updatedAt: '2026-07-26 16:00' },
  'TK202607200004': { ticketNo: 'TK202607200004', title: 'test-云平台私有化部署', category: 'techSupport', priority: 'low', status: 'closed', appValue: '', projectName: '已归档项目', description: '确认私有化部署的服务器配置和环境要求。', attachments: [{ name: '配置清单.xlsx' }], createdAt: '2026-07-20 08:30', updatedAt: '2026-07-23 09:00' },
  'TK202607270005': { ticketNo: 'TK202607270005', title: 'test-API token认证失败', category: 'techSupport', priority: 'normal', status: 'pending', appValue: 'app_hs-iOS-OV', projectName: 'SDK联调项目', description: '调用平台API时token认证返回401。', attachments: [], createdAt: '2026-07-27 10:30', updatedAt: '2026-07-27 10:30' },
};

const MOCK_REMARKS = {
  'TK202607270001': [
    { id: '1', type: 'enterprise', author: '我', content: '调用SDK初始化接口时返回错误码1001...', createdAt: '2026-07-27 09:15' },
  ],
  'TK202607260002': [
    { id: '1', type: 'enterprise', author: '我', content: '设备固件升级失败，设备变砖。', createdAt: '2026-07-26 14:30' },
    { id: '2', type: 'system', author: '系统', content: '客服领取了工单', createdAt: '2026-07-26 15:00' },
    { id: '3', type: 'staff', author: '客服', content: '正在排查固件版本兼容性问题，请稍候。', createdAt: '2026-07-27 10:00' },
  ],
  'TK202607250003': [
    { id: '1', type: 'enterprise', author: '我', content: '希望在App OEM定制中增加品牌配置页面。', createdAt: '2026-07-25 11:00' },
    { id: '2', type: 'system', author: '系统', content: '客服领取了工单', createdAt: '2026-07-25 14:00' },
    { id: '3', type: 'staff', author: '客服', content: '已评估，品牌配置页加入下个迭代计划，预计周三上线。', createdAt: '2026-07-26 10:00' },
    { id: '4', type: 'system', author: '系统', content: '客服将状态变为〔已解决〕', createdAt: '2026-07-26 16:00' },
  ],
  'TK202607200004': [
    { id: '1', type: 'enterprise', author: '我', content: '确认私有化部署的服务器配置。', createdAt: '2026-07-20 08:30' },
    { id: '2', type: 'system', author: '系统', content: '客服领取了工单', createdAt: '2026-07-20 10:00' },
    { id: '3', type: 'staff', author: '客服', content: '已提供部署文档和环境要求。', createdAt: '2026-07-22 15:00' },
    { id: '4', type: 'system', author: '系统', content: '客服将状态变为〔已解决〕', createdAt: '2026-07-22 17:00' },
    { id: '5', type: 'system', author: '系统', content: '客服关闭了工单', createdAt: '2026-07-23 09:00' },
  ],
  'TK202607270005': [
    { id: '1', type: 'enterprise', author: '我', content: 'API token认证失败返回401。', createdAt: '2026-07-27 10:30' },
  ],
};

// ══════════════════════════════════════════════
//  工单详情（企业视角）
// ══════════════════════════════════════════════

function TicketMyDetail() {
  const { ticketNo } = useParams();
  const navigate = useNavigate();

  const initialTicket = MOCK_TICKETS[ticketNo];
  const [ticket, setTicket] = useState(() => initialTicket || null);
  const [remarks, setRemarks] = useState(() => MOCK_REMARKS[ticketNo] || []);
  const [editing, setEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [editForm] = Form.useForm();

  // ==================== 确认解决 ====================
  const handleConfirm = useCallback(() => {
    Modal.confirm({
      title: '确认问题已解决？',
      content: '确认后工单将关闭，不可再操作。',
      onOk: () => {
        const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
        setTicket(prev => ({ ...prev, status: 'closed', updatedAt: now }));
        setRemarks(prev => [
          { id: Date.now().toString(), type: 'system', author: '系统', content: '企业确认解决，工单已关闭', createdAt: now },
          ...prev,
        ]);
        message.success('已确认解决，工单已关闭');
      },
    });
  }, []);

  // ==================== 重新打开 ====================
  const handleReopen = useCallback(() => {
    Modal.confirm({
      title: '确认重新打开此工单？',
      onOk: () => {
        const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
        setTicket(prev => ({ ...prev, status: 'processing', updatedAt: now }));
        setRemarks(prev => [
          { id: Date.now().toString(), type: 'system', author: '系统', content: '企业重新打开了工单', createdAt: now },
          ...prev,
        ]);
        message.success('工单已重新打开');
      },
    });
  }, []);

  // ==================== 编辑 ====================
  const handleEdit = useCallback(() => {
    editForm.setFieldsValue({
      title: ticket.title,
      category: ticket.category,
      priority: ticket.priority,
      description: ticket.description,
    });
    setEditing(true);
  }, [ticket, editForm]);

  const handleSaveEdit = useCallback(async () => {
    try {
      const values = await editForm.validateFields();
      const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
      setTicket(prev => ({
        ...prev,
        title: values.title,
        category: values.category,
        priority: values.priority,
        description: values.description,
        updatedAt: now,
      }));
      setRemarks(prev => [
        { id: Date.now().toString(), type: 'system', author: '系统', content: '企业修改了工单信息', createdAt: now },
        ...prev,
      ]);
      setEditing(false);
      message.success('保存成功');
    } catch {
      // 表单校验失败
    }
  }, [editForm]);

  const handleCancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  // ==================== 删除 ====================
  const handleDelete = useCallback(() => {
    Modal.confirm({
      title: '确认删除',
      content: '确认删除该工单？删除后不可恢复',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        setIsDeleted(true);
        message.success('工单已删除');
        setTimeout(() => navigate('/customer/ticket/my'), 1000);
      },
    });
  }, [navigate]);

  // ==================== 关闭工单 ====================
  const handleClose = useCallback(() => {
    Modal.confirm({
      title: '确认关闭',
      content: '确认关闭该工单？关闭后不可重新打开',
      onOk: () => {
        const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
        setTicket(prev => ({ ...prev, status: 'closed', updatedAt: now }));
        setRemarks(prev => [
          { id: Date.now().toString(), type: 'system', author: '系统', content: '企业关闭了工单', createdAt: now },
          ...prev,
        ]);
        message.success('工单已关闭');
      },
    });
  }, []);

  // ==================== 已删除状态 ====================
  if (isDeleted || (!ticket && !initialTicket)) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="info"
          title="工单已被删除"
          subTitle="该工单已被删除，无法查看详情"
          extra={<Button type="primary" onClick={() => navigate('/customer/ticket/my')}>返回工单列表</Button>}
        />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{ padding: 24 }}>
        <Result status="404" title="工单不存在" extra={<Button onClick={() => navigate('/customer/ticket/my')}>返回工单列表</Button>} />
      </div>
    );
  }

  const statusInfo = STATUS_MAP[ticket.status];
  const priorityInfo = PRIORITY_MAP[ticket.priority];
  const isPending = ticket.status === 'pending';
  const isProcessing = ticket.status === 'processing';
  const isResolved = ticket.status === 'resolved';
  const isClosed = ticket.status === 'closed';

  return (
    <div style={{ padding: 24 }}>
      <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate('/customer/ticket/my')}>
        返回工单列表
      </Button>

      {editing ? (
        /* ========== 编辑模式 ========== */
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16, maxWidth: 900 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              <Text code style={{ fontSize: 14, marginRight: 8 }}>{ticket.ticketNo}</Text>
              编辑工单
            </Title>
            <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ fontSize: 14, padding: '2px 12px', border: 'none' }}>
              {statusInfo.label}
            </Tag>
          </div>

          <Form form={editForm} layout="vertical" requiredMark={false}>
            <Form.Item name="title" label="工单标题"
              rules={[{ required: true, message: '请填写工单标题' }, { max: 100, message: '标题不超过100字' }]}>
              <Input placeholder="请输入工单标题" maxLength={100} showCount />
            </Form.Item>
            <Form.Item name="category" label="工单分类"
              rules={[{ required: true, message: '请选择工单分类' }]}>
              <Select style={{ width: 160 }}>
                <Option value="techSupport">技术对接</Option>
                <Option value="bug">BUG</Option>
                <Option value="requirement">需求</Option>
                <Option value="other">其他</Option>
              </Select>
            </Form.Item>
            <Form.Item name="priority" label="优先级">
              <Select style={{ width: 160 }}>
                <Option value="low">低</Option>
                <Option value="normal">普通</Option>
                <Option value="high">高</Option>
                <Option value="urgent">紧急</Option>
              </Select>
            </Form.Item>
            <Form.Item name="description" label="问题描述"
              rules={[{ required: true, message: '请填写问题描述' }, { min: 10, message: '问题描述不少于10字' }]}>
              <TextArea rows={6} placeholder="请详细描述您遇到的问题" maxLength={5000} showCount />
            </Form.Item>
            <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <Button type="primary" onClick={handleSaveEdit}>保存</Button>
              <Button onClick={handleCancelEdit}>取消</Button>
            </div>
          </Form>
        </Card>
      ) : (
        /* ========== 只读模式：工单信息 ========== */
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16, maxWidth: 900 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              <Text code style={{ fontSize: 14, marginRight: 8 }}>{ticket.ticketNo}</Text>
              {ticket.title}
            </Title>
            <Space size={8}>
              {isPending && (
                <>
                  <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>编辑</Button>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDelete}>删除</Button>
                </>
              )}
              <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ fontSize: 14, padding: '2px 12px', border: 'none' }}>
                {statusInfo.label}
              </Tag>
            </Space>
          </div>

          <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="工单分类">{CATEGORY_LABEL[ticket.category]}</Descriptions.Item>
            <Descriptions.Item label="优先级">
              {priorityInfo ? <Tag color={priorityInfo.color} style={{ border: 'none' }}>{priorityInfo.label}</Tag> : ticket.priority}
            </Descriptions.Item>
            <Descriptions.Item label="所属App">
              {ticket.appValue ? (APP_DISPLAY_MAP[ticket.appValue] || ticket.appValue) : <span style={{ color: '#ccc' }}>-</span>}
            </Descriptions.Item>
            <Descriptions.Item label="所属项目">
              {ticket.projectName || <span style={{ color: '#ccc' }}>-</span>}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{ticket.createdAt}</Descriptions.Item>
            <Descriptions.Item label="最近更新">{ticket.updatedAt}</Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '12px 0' }} />
          <Title level={5} style={{ marginTop: 0 }}>问题描述</Title>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Paragraph>

          {ticket.attachments?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong>附件：</Text>
              {ticket.attachments.map((f, i) => <Tag key={i} style={{ marginLeft: 8 }}>📄 {f.name}</Tag>)}
            </div>
          )}
        </Card>
      )}

      {/* 处理进展 */}
      {!editing && (
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16, maxWidth: 900 }}
          title="处理进展">
          {remarks.length === 0 ? (
            <Empty description="暂无处理记录" />
          ) : (
            <Timeline items={remarks.map(rmk => {
              const typeInfo = REMARK_TYPE_MAP[rmk.type];
              return {
                color: rmk.type === 'system' ? 'gray' : (rmk.type === 'staff' ? 'green' : 'blue'),
                children: (
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{rmk.createdAt}</Text>
                      <Tag color={typeInfo.color} style={{ border: 'none', marginLeft: 8, fontSize: 11 }}>{typeInfo.label}</Tag>
                      <Text strong style={{ marginLeft: 4 }}>{rmk.author}</Text>
                    </div>
                    <div style={{ color: rmk.type === 'system' ? '#999' : '#333', fontSize: rmk.type === 'system' ? 13 : 14, whiteSpace: 'pre-wrap' }}>
                      {rmk.content}
                    </div>
                  </div>
                ),
              };
            })} />
          )}
        </Card>
      )}

      {/* 底部操作按钮 */}
      {!editing && !isClosed && (
        <Space size={12}>
          {isProcessing && (
            <Button danger icon={<CloseCircleOutlined />} size="large" onClick={handleClose}>
              关闭工单
            </Button>
          )}
          {isResolved && (
            <>
              <Button type="primary" icon={<CheckCircleOutlined />} size="large" onClick={handleConfirm}>
                确认解决
              </Button>
              <Button icon={<UndoOutlined />} size="large" onClick={handleReopen}>
                重新打开
              </Button>
              <Button danger icon={<CloseCircleOutlined />} size="large" onClick={handleClose}>
                关闭工单
              </Button>
            </>
          )}
        </Space>
      )}
    </div>
  );
}

export default TicketMyDetail;
